/**
 * Offer Form Validation Library
 * Provides security validation for email inputs and offer amounts
 */

// Rate limiting configuration
const OfferValidation = {
  lastSubmission: 0,
  RATE_LIMIT_MS: 5000, // 5 seconds between submissions
  
  /**
   * Validates email address format and security
   * @param {string} email - Email address to validate
   * @returns {object} - {isValid: boolean, error: string}
   */
  validateEmail(email) {
    if (!email || typeof email !== 'string') {
      return { isValid: false, error: 'Email is required' };
    }

    // Length check (RFC 5321 limit)
    if (email.length > 254) {
      return { isValid: false, error: 'Email address is too long' };
    }

    if (email.length < 5) {
      return { isValid: false, error: 'Email address is too short' };
    }

    // Basic email regex pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, error: 'Please enter a valid email address' };
    }

    // Check for dangerous characters and common injection patterns
    const dangerousChars = /<|>|"|'|&|javascript:|data:|vbscript:|onload|onerror|script|iframe/i;
    if (dangerousChars.test(email)) {
      return { isValid: false, error: 'Email contains invalid characters' };
    }

    // Check for multiple @ symbols
    if ((email.match(/@/g) || []).length !== 1) {
      return { isValid: false, error: 'Email format is invalid' };
    }

    return { isValid: true, error: null };
  },

  /**
   * Validates offer amount
   * @param {string|number} offer - Offer amount to validate
   * @param {number} fullPrice - Full price in cents (optional)
   * @returns {object} - {isValid: boolean, error: string, value: number}
   */
  validateOffer(offer, fullPrice = null) {
    if (!offer && offer !== 0) {
      return { isValid: false, error: 'Offer amount is required', value: null };
    }

    const numericOffer = parseFloat(offer);
    
    if (isNaN(numericOffer)) {
      return { isValid: false, error: 'Please enter a valid number', value: null };
    }

    if (numericOffer <= 0) {
      return { isValid: false, error: 'Offer must be greater than $0', value: null };
    }

    if (numericOffer > 999999) {
      return { isValid: false, error: 'Offer amount is too large', value: null };
    }

    // Check for reasonable decimal places (max 2)
    const decimalPlaces = (numericOffer.toString().split('.')[1] || '').length;
    if (decimalPlaces > 2) {
      return { isValid: false, error: 'Please enter amount with maximum 2 decimal places', value: null };
    }

    // Check against full price if provided
    if (fullPrice !== null && fullPrice > 0) {
      // fullPrice is already in dollars, so compare directly
      if (numericOffer > fullPrice) {
        const formattedPrice = fullPrice.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD'
        });
        return { 
          isValid: false, 
          error: `Offer cannot exceed the full price of ${formattedPrice}`, 
          value: null 
        };
      }
    }

    return { isValid: true, error: null, value: numericOffer };
  },

  /**
   * Sanitizes input by removing dangerous characters
   * @param {string} input - Input to sanitize
   * @returns {string} - Sanitized input
   */
  sanitizeInput(input) {
    if (!input || typeof input !== 'string') return '';
    return input.toString().trim().replace(/[<>"'&]/g, '');
  },

  /**
   * Checks if enough time has passed since last submission
   * @returns {object} - {canSubmit: boolean, waitTime: number}
   */
  checkRateLimit() {
    const now = Date.now();
    const timeSinceLastSubmission = now - this.lastSubmission;
    
    if (timeSinceLastSubmission < this.RATE_LIMIT_MS) {
      const waitTime = Math.ceil((this.RATE_LIMIT_MS - timeSinceLastSubmission) / 1000);
      return { 
        canSubmit: false, 
        waitTime,
        error: `Please wait ${waitTime} seconds before submitting another offer`
      };
    }

    return { canSubmit: true, waitTime: 0, error: null };
  },

  /**
   * Sets the rate limit timestamp
   */
  setRateLimit() {
    this.lastSubmission = Date.now();
  },

  /**
   * Validates all form inputs at once
   * @param {string} offerInput - Offer amount input
   * @param {string} emailInput - Email input
   * @param {number} fullPrice - Full price in cents (optional)
   * @returns {object} - Complete validation result
   */
  validateForm(offerInput, emailInput, fullPrice = null) {
    const result = {
      isValid: true,
      errors: [],
      offer: null,
      email: null
    };

    // Check rate limit first
    const rateLimitCheck = this.checkRateLimit();
    if (!rateLimitCheck.canSubmit) {
      result.isValid = false;
      result.errors.push(rateLimitCheck.error);
      return result;
    }

    // Get fullPrice from global offerData if not provided and available
    const priceToCheck = fullPrice || (typeof window !== 'undefined' && window.offerData ? window.offerData.fullPrice : null);

    // Validate offer
    const offerValidation = this.validateOffer(offerInput, priceToCheck);
    if (!offerValidation.isValid) {
      result.isValid = false;
      result.errors.push(offerValidation.error);
    } else {
      result.offer = offerValidation.value;
    }

    // Sanitize and validate email
    const sanitizedEmail = this.sanitizeInput(emailInput);
    const emailValidation = this.validateEmail(sanitizedEmail);
    if (!emailValidation.isValid) {
      result.isValid = false;
      result.errors.push(emailValidation.error);
    } else {
      result.email = sanitizedEmail;
    }

    return result;
  },

  /**
   * Validates URL for security (for checkout URLs)
   * @param {string} url - URL to validate
   * @returns {object} - {isValid: boolean, error: string}
   */
  validateUrl(url) {
    if (!url || typeof url !== 'string') {
      return { isValid: false, error: 'Invalid URL' };
    }

    try {
      const urlObj = new URL(url);
      
      // Only allow HTTPS URLs
      if (urlObj.protocol !== 'https:') {
        return { isValid: false, error: 'URL must use HTTPS' };
      }

      // Basic hostname validation (you might want to restrict to specific domains)
      if (!urlObj.hostname || urlObj.hostname.length < 3) {
        return { isValid: false, error: 'Invalid hostname' };
      }

      return { isValid: true, error: null };
    } catch (e) {
      return { isValid: false, error: 'Invalid URL format' };
    }
  }
};

// Make it available globally
if (typeof window !== 'undefined') {
  window.OfferValidation = OfferValidation;
}

// Export for Node.js environments
if (typeof module !== 'undefined' && module.exports) {
  module.exports = OfferValidation;
}